/**
 * Obstacle System Types
 * Physical obstacles inside building units that participate in collision detection
 */

import type { Database } from '@planmax/shared-types'

// --------------------------------------------------------------------------
// Enums (matching database)
// --------------------------------------------------------------------------

export type ObstacleShape = Database['public']['Enums']['obstacle_shape']

export type ObstacleMobility = Database['public']['Enums']['obstacle_mobility']

export type ObstacleType =
  | 'column'
  | 'wall'
  | 'firewall'
  | 'office'
  | 'storage'
  | 'restroom'
  | 'equipment'
  | 'vehicle'
  | 'staging'
  | 'barrier'
  | 'utility'
  | 'other'

export type ObstacleStatus = 'active' | 'inactive' | 'archived'

// --------------------------------------------------------------------------
// Visibility (computed client-side from scheduling fields)
// --------------------------------------------------------------------------

export type ObstacleVisibility = 'visible' | 'scheduled' | 'expired'

// --------------------------------------------------------------------------
// Database row types
// --------------------------------------------------------------------------

export type ObstacleRow = Database['public']['Tables']['obstacles']['Row']
export type ObstacleInsert = Database['public']['Tables']['obstacles']['Insert']
export type ObstacleUpdate = Database['public']['Tables']['obstacles']['Update']

export type ObstaclePresetRow = Database['public']['Tables']['obstacle_presets']['Row']

// --------------------------------------------------------------------------
// Frontend domain types
// --------------------------------------------------------------------------

export interface Obstacle {
  id: string
  organizationId: string
  locationId: string
  unitId: string

  // Identity
  name: string
  obstacleType: ObstacleType
  mobility: ObstacleMobility
  shape: ObstacleShape

  // Geometry (unit-local, feet)
  positionX: number
  positionY: number
  rotation: number
  width: number | null
  depth: number | null
  radius: number | null
  height: number
  elevation: number

  // Scheduling
  effectiveFrom: string | null
  effectiveUntil: string | null
  isActive: boolean

  // Grouping
  groupId: string | null
  groupLabel: string | null

  // Visual
  color: string
  opacity: number
  labelVisible: boolean

  // Collision
  participatesInCollision: boolean
  clearanceBufferFt: number

  // Metadata
  notes: string | null
  status: ObstacleStatus
}

export interface ObstaclePreset {
  id: string
  organizationId: string | null
  name: string
  obstacleType: ObstacleType
  shape: ObstacleShape
  mobility: ObstacleMobility
  width: number | null
  depth: number | null
  radius: number | null
  height: number
  elevation: number
  color: string
  opacity: number
  participatesInCollision: boolean
  clearanceBufferFt: number
  isSystem: boolean
  sortOrder: number
}

// --------------------------------------------------------------------------
// Input types for mutations
// --------------------------------------------------------------------------

export interface CreateObstacleInput {
  unitId: string
  locationId: string
  name: string
  obstacleType: ObstacleType
  mobility?: ObstacleMobility
  shape?: ObstacleShape
  positionX: number
  positionY: number
  rotation?: number
  width?: number | null
  depth?: number | null
  radius?: number | null
  height: number
  elevation?: number
  effectiveFrom?: string | null
  effectiveUntil?: string | null
  isActive?: boolean
  groupId?: string | null
  groupLabel?: string | null
  color?: string
  opacity?: number
  labelVisible?: boolean
  participatesInCollision?: boolean
  clearanceBufferFt?: number
  notes?: string | null
}

export interface UpdateObstacleInput {
  name?: string
  obstacleType?: ObstacleType
  mobility?: ObstacleMobility
  shape?: ObstacleShape
  positionX?: number
  positionY?: number
  rotation?: number
  width?: number | null
  depth?: number | null
  radius?: number | null
  height?: number
  elevation?: number
  effectiveFrom?: string | null
  effectiveUntil?: string | null
  isActive?: boolean
  groupId?: string | null
  groupLabel?: string | null
  color?: string
  opacity?: number
  labelVisible?: boolean
  participatesInCollision?: boolean
  clearanceBufferFt?: number
  notes?: string | null
  status?: ObstacleStatus
}

// --------------------------------------------------------------------------
// Helpers
// --------------------------------------------------------------------------

/** Map DB row to frontend Obstacle */
export function mapObstacleRow(row: ObstacleRow): Obstacle {
  return {
    id: row.id,
    organizationId: row.organization_id,
    locationId: row.location_id,
    unitId: row.unit_id,
    name: row.name,
    obstacleType: row.obstacle_type as ObstacleType,
    mobility: row.mobility,
    shape: row.shape,
    positionX: Number(row.position_x),
    positionY: Number(row.position_y),
    rotation: Number(row.rotation),
    width: row.width != null ? Number(row.width) : null,
    depth: row.depth != null ? Number(row.depth) : null,
    radius: row.radius != null ? Number(row.radius) : null,
    height: Number(row.height),
    elevation: Number(row.elevation),
    effectiveFrom: row.effective_from,
    effectiveUntil: row.effective_until,
    isActive: row.is_active,
    groupId: row.group_id,
    groupLabel: row.group_label,
    color: row.color ?? '#808080',
    opacity: Number(row.opacity ?? 1),
    labelVisible: row.label_visible,
    participatesInCollision: row.participates_in_collision,
    clearanceBufferFt: Number(row.clearance_buffer_ft),
    notes: row.notes,
    status: row.status as ObstacleStatus,
  }
}

/** Map DB preset row to frontend ObstaclePreset */
export function mapPresetRow(row: ObstaclePresetRow): ObstaclePreset {
  return {
    id: row.id,
    organizationId: row.organization_id,
    name: row.name,
    obstacleType: row.obstacle_type as ObstacleType,
    shape: row.shape,
    mobility: row.mobility,
    width: row.width != null ? Number(row.width) : null,
    depth: row.depth != null ? Number(row.depth) : null,
    radius: row.radius != null ? Number(row.radius) : null,
    height: Number(row.height),
    elevation: Number(row.elevation),
    color: row.color ?? '#808080',
    opacity: Number(row.opacity ?? 1),
    participatesInCollision: row.participates_in_collision,
    clearanceBufferFt: Number(row.clearance_buffer_ft),
    isSystem: row.is_system,
    sortOrder: row.sort_order,
  }
}

/** Compute obstacle visibility from scheduling fields */
export function getObstacleVisibility(obstacle: Obstacle): ObstacleVisibility {
  if (!obstacle.isActive) return 'expired'

  const now = Date.now()

  if (obstacle.effectiveFrom) {
    const from = new Date(obstacle.effectiveFrom).getTime()
    if (now < from) return 'scheduled'
  }

  if (obstacle.effectiveUntil) {
    const until = new Date(obstacle.effectiveUntil).getTime()
    if (now > until) return 'expired'
  }

  return 'visible'
}

/** Check if obstacle should be rendered in 3D scene */
export function isObstacleCurrentlyVisible(obstacle: Obstacle): boolean {
  return getObstacleVisibility(obstacle) === 'visible'
}

// --------------------------------------------------------------------------
// Obstacle type metadata for UI
// --------------------------------------------------------------------------

export const OBSTACLE_TYPE_LABELS: Record<ObstacleType, string> = {
  column: 'Column',
  wall: 'Wall',
  firewall: 'Firewall',
  office: 'Office',
  storage: 'Storage',
  restroom: 'Restroom',
  equipment: 'Equipment',
  vehicle: 'Vehicle',
  staging: 'Staging Area',
  barrier: 'Barrier',
  utility: 'Utility',
  other: 'Other',
}

export const OBSTACLE_MOBILITY_LABELS: Record<ObstacleMobility, string> = {
  static: 'Static',
  semi_permanent: 'Semi-Permanent',
  dynamic: 'Dynamic',
}
